import React, { FC, RefObject, useImperativeHandle, useState } from "react";
import { darkTheme } from "@yleisradio/yds-core";
import { Close, Share } from "@yleisradio/yds-icons-react";
import styled from "styled-components";

import { UI_SIZE } from "../../enums";
import { buttonStyle } from "../../styles/global";
import { focusColor } from "../../styles/variables";
import { FadingComponent } from "../FadingComponent";
import {
  OverlayButtonWrapper,
  overlayLinkOrButtonStyle,
  shareButtonStyle,
  textButtonStyle
} from "../VideoOverlayButton";

const ShareCloseButton = styled.button`
  ${buttonStyle}
  ${shareButtonStyle}
  ${overlayLinkOrButtonStyle}
  background-color: rgb(0 0 0 / 0%);
  z-index: 210;

  &:focus-visible {
    outline: 1px solid ${focusColor} !important;
  }
`;

const ShareButtonWithText = styled.button<{ uiSize: UI_SIZE }>`
  ${buttonStyle}
  ${shareButtonStyle}
  ${overlayLinkOrButtonStyle}
  ${textButtonStyle}
  background-color: rgb(0 0 0 / 70%);

  &:focus-visible {
    outline: 1px solid ${focusColor} !important;
  }
`;

export interface ShareOverlayButtonRef {
  closeButton: HTMLButtonElement | null;
  openButton: HTMLButtonElement | null;
}

interface Props {
  buttonVisible: boolean;
  overlayVisible: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  label: string;
  closeLinkOnClick: (
    event:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.KeyboardEvent<HTMLButtonElement>
  ) => void;
  openLinkOnClick: (
    event:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.KeyboardEvent<HTMLButtonElement>
  ) => void;
  uiSize: UI_SIZE;
  closeLabel: string;
  ref: RefObject<ShareOverlayButtonRef | null>;
}
export const ShareOverlayButton: FC<Props> = ({
  buttonVisible,
  closeLabel,
  closeLinkOnClick,
  label,
  onMouseEnter,
  onMouseLeave,
  openLinkOnClick,
  overlayVisible,
  ref,
  uiSize
}) => {
  const [openButton, setOpenButton] = useState<HTMLButtonElement | null>(null);
  const [closeButton, setCloseButton] = useState<HTMLButtonElement | null>(
    null
  );

  useImperativeHandle(
    ref,
    () => ({
      closeButton,
      openButton
    }),
    [openButton, closeButton]
  );

  return (
    <FadingComponent isVisible={buttonVisible}>
      <OverlayButtonWrapper
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}>
        {overlayVisible ? (
          <ShareCloseButton
            ref={setCloseButton}
            onClick={e => {
              e.stopPropagation();
              closeLinkOnClick(e);
            }}
            aria-label={closeLabel}>
            <Close color={darkTheme.ACTION_PRIMARY} />
          </ShareCloseButton>
        ) : (
          <ShareButtonWithText
            ref={setOpenButton}
            aria-label={label}
            onClick={openLinkOnClick}
            onKeyDown={e => {
              if (e.key === " ") {
                e.preventDefault();
                openLinkOnClick(e);
              }
            }}
            uiSize={uiSize}
            tabIndex={buttonVisible ? 0 : -1}>
            <span>{label}</span>
            <Share color={darkTheme.ACTION_PRIMARY} />
          </ShareButtonWithText>
        )}
      </OverlayButtonWrapper>
    </FadingComponent>
  );
};
